import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AddOrderDialog } from '@/pages/OrdersPage';
import { ClientDetailSheet } from '@/components/clients/ClientDetailSheet';
import { BroadcastDialog } from '@/components/clients/BroadcastDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Plus,
  Search,
  Loader2,
  Users,
  Phone,
  Mail,
  MapPin,
  MoreHorizontal,
  Pencil,
  Trash2,
  StickyNote,
  Tag,
  X,
  DollarSign,
  Briefcase,
  AlertCircle,
  CheckCircle,
  Send
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';

// Add Client Dialog
export function AddClientDialog({ open, onOpenChange, onSuccess, editClient = null }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    phone_secondary: '',
    street: '',
    city: '',
    postal_code: '',
    tags: '',
    pinned_note: '',
    notes: '',
  });

  // Reset form when dialog opens/closes or editClient changes
  useEffect(() => {
    if (editClient) {
      setFormData({
        name: editClient.name || '',
        email: editClient.email || '',
        phone: editClient.phone || '',
        phone_secondary: editClient.phone_secondary || '',
        street: editClient.address?.street || '',
        city: editClient.address?.city || '',
        postal_code: editClient.address?.postal_code || '',
        tags: editClient.tags?.join(', ') || '',
        pinned_note: editClient.pinned_note || '',
        notes: editClient.notes || '',
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        phone_secondary: '',
        street: '',
        city: '',
        postal_code: '',
        tags: '',
        pinned_note: '',
        notes: '',
      });
    }
    setError('');
  }, [editClient, open]);

  // Validation function for client data
  const validateClientData = () => {
    const errors = [];
    const warnings = [];
    
    // Name: min 2 chars + space + min 2 chars
    if (!formData.name || formData.name.trim().length < 5) {
      errors.push('Имя: минимум 5 символов');
    } else {
      const parts = formData.name.trim().split(/\s+/);
      if (parts.length < 2) {
        errors.push('Имя: укажите имя и фамилию через пробел');
      } else if (parts[0].length < 2 || parts[1].length < 2) {
        errors.push('Имя: минимум 2 буквы в имени и 2 в фамилии');
      }
    }
    
    // Phone: min 10 digits
    if (!formData.phone) {
      errors.push('Телефон: обязательное поле');
    } else {
      const digits = formData.phone.replace(/\D/g, '');
      if (digits.length < 10) {
        errors.push(`Телефон: минимум 10 цифр (сейчас ${digits.length})`);
      }
    }
    
    // Email: min 5 chars + @
    if (!formData.email) {
      errors.push('Email: обязательное поле');
    } else if (formData.email.length < 5) {
      errors.push('Email: минимум 5 символов');
    } else if (!formData.email.includes('@')) {
      errors.push('Email: должен содержать @');
    }
    
    // Address: min 1 digit + min 2 letters
    if (!formData.street) {
      errors.push('Адрес: обязательное поле');
    } else {
      const digitsInAddr = (formData.street.match(/\d/g) || []).length;
      const lettersInAddr = (formData.street.match(/[a-zA-Zа-яА-ЯёЁ]/g) || []).length;
      if (digitsInAddr < 1) {
        errors.push('Адрес: должен содержать минимум 1 цифру (номер дома)');
      }
      if (lettersInAddr < 2) {
        errors.push('Адрес: должен содержать минимум 2 буквы');
      }
    }
    
    // Postal code: warning only
    if (!formData.postal_code) {
      warnings.push('Почтовый код не заполнен');
    }
    
    return { errors, warnings };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Frontend validation
    const { errors, warnings } = validateClientData();
    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }
    
    // Show warnings but don't block
    if (warnings.length > 0) {
      console.log('Warnings:', warnings);
    }
    
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        phone_secondary: formData.phone_secondary || null,
        address: (formData.street || formData.city || formData.postal_code) ? {
          street: formData.street || null,
          city: formData.city || null,
          postal_code: formData.postal_code || null,
        } : null,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        pinned_note: formData.pinned_note || null,
        notes: formData.notes || null,
      };
      
      let result = null;
      if (editClient) {
        const res = await api.patch(`/clients/${editClient.id}`, payload);
        result = res.data;
      } else {
        const res = await api.post('/clients', payload);
        result = res.data;
      }

      onSuccess(result);
      onOpenChange(false);
    } catch (err) {
      // Handle backend validation errors
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.errors) {
        setError(detail.errors.join('\n'));
      } else {
        setError(detail || 'Failed to save client');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-auto bottom-0 translate-x-0 translate-y-0 sm:top-[50%] sm:translate-y-[-50%] sm:translate-x-[-50%] sm:bottom-auto left-0 right-0 sm:left-[50%] sm:right-auto w-full sm:w-auto sm:max-w-xl rounded-t-2xl rounded-b-none sm:rounded-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editClient ? t('clients.editClient') : t('clients.addClient')}</DialogTitle>
          <DialogDescription>
            {editClient ? t('clients.editClient') : t('clients.subtitle')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="h-4 w-4" />
                  <span className="font-medium">Ошибки валидации:</span>
                </div>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  {error.split('\n').map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">{t('clients.name')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder={t('clients.namePlaceholder')}
                required
                data-testid="client-name"
              />
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('clients.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="client@example.com"
                  data-testid="client-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{t('clients.phone')}</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+7 999 123 4567"
                  data-testid="client-phone"
                />
              </div>
            </div>

            {/* Address with autocomplete */}
            <div className="space-y-2">
              <Label>Адрес</Label>
              <AddressAutocomplete
                value={formData.street}
                onChange={(val) => setFormData(prev => ({...prev, street: val}))}
                onSelect={(result) => {
                  const c = result.components || {};
                  setFormData(prev => ({
                    ...prev,
                    street: c.housenumber
                      ? `${c.housenumber} ${c.street || ''}`.trim()
                      : c.street || result.address,
                    city: c.city || c.state || prev.city,
                    postal_code: c.postcode || prev.postal_code,
                  }));
                }}
                placeholder="Начните вводить адрес..."
                data-testid="client-street"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder="Город"
                  data-testid="client-city"
                />
                <Input
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  placeholder="Индекс"
                  data-testid="client-postal"
                />
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label htmlFor="tags">Теги (через запятую)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                placeholder="vip, уборка, постоянный"
                data-testid="client-tags"
              />
            </div>

            {/* Pinned Note */}
            <div className="space-y-2">
              <Label htmlFor="pinned_note" className="flex items-center gap-2">
                <StickyNote className="h-4 w-4 text-amber-500" />
                Важная заметка (всегда видна)
              </Label>
              <Textarea
                id="pinned_note"
                value={formData.pinned_note}
                onChange={(e) => setFormData({...formData, pinned_note: e.target.value})}
                placeholder="Важная информация о клиенте..."
                rows={2}
                maxLength={500}
                data-testid="client-pinned-note"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Заметки</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Дополнительная информация..."
                rows={3}
                data-testid="client-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={loading} data-testid="client-submit">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
              {editClient ? 'Сохранить' : 'Создать'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ClientDetailSheet is now imported from @/components/clients/ClientDetailSheet

// Client Row Actions
function ClientActions({ client, onEdit, onView }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`client-actions-${client.id}`}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onView(client)}>
          <Users className="mr-2 h-4 w-4" />
          Открыть карточку
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onEdit(client)}>
          <Pencil className="mr-2 h-4 w-4" />
          Редактировать
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-muted-foreground">
          <Briefcase className="mr-2 h-4 w-4" />
          Заказы (скоро)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Main Clients Page
export default function ClientsPage() {
  const { api, hasRole } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [viewClient, setViewClient] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderClient, setOrderClient] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      
      const response = await api.get(`/clients?${params.toString()}`);
      setClients(response.data);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    } finally {
      setLoading(false);
    }
  }, [api, searchQuery]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchClients();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchClients]);

  const handleEdit = (client) => {
    setEditClient(client);
    setDialogOpen(true);
  };

  const handleView = (client) => {
    setViewClient(client);
    setDetailOpen(true);
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) setEditClient(null);
  };

  const handleCreateOrder = (client) => {
    setOrderClient(client);
    setOrderDialogOpen(true);
  };

  const handleOrderCreated = () => {
    fetchClients(); // Refresh to update orders_count
    setOrderDialogOpen(false);
    setOrderClient(null);
  };

  const canManage = hasRole('admin') || hasRole('manager');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Клиенты</h1>
          <p className="text-muted-foreground mt-1">
            База клиентов компании
          </p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setBroadcastOpen(true)}>
              <Send className="mr-2 h-4 w-4" />
              Рассылка
            </Button>
            <Button onClick={() => setDialogOpen(true)} data-testid="add-client-btn">
              <Plus className="mr-2 h-4 w-4" />
              Добавить клиента
            </Button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Поиск по имени, email, телефону, адресу..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="clients-search"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => setSearchQuery('')}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Clients List */}
      <Card className="cgc-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>{searchQuery ? 'Клиенты не найдены' : 'Нет клиентов'}</p>
              {canManage && !searchQuery && (
                <Button 
                  variant="link" 
                  onClick={() => setDialogOpen(true)}
                  className="mt-2"
                >
                  Добавить первого клиента
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[300px]">Клиент</TableHead>
                  <TableHead>Контакты</TableHead>
                  <TableHead>Теги</TableHead>
                  <TableHead className="text-right">Баланс</TableHead>
                  <TableHead className="text-center">Заказов</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => (
                  <TableRow 
                    key={client.id} 
                    data-testid={`client-row-${client.id}`}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleView(client)}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {client.name}
                          {!client.is_active && (
                            <Badge variant="secondary" className="text-xs">Неактивен</Badge>
                          )}
                        </p>
                        {client.address_string && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            {client.address_string}
                          </p>
                        )}
                        {client.pinned_note && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 mt-1">
                            <StickyNote className="h-3 w-3" />
                            {client.pinned_note.length > 50 
                              ? client.pinned_note.slice(0, 50) + '...' 
                              : client.pinned_note}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="space-y-1">
                        {client.phone && (
                          <a 
                            href={`tel:${client.phone}`} 
                            className="text-sm flex items-center gap-1 hover:text-accent"
                          >
                            <Phone className="h-3 w-3" />
                            {client.phone}
                          </a>
                        )}
                        {client.email && (
                          <a 
                            href={`mailto:${client.email}`}
                            className="text-sm flex items-center gap-1 hover:text-accent"
                          >
                            <Mail className="h-3 w-3" />
                            {client.email}
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {client.tags && client.tags.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {client.tags.slice(0, 2).map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {client.tags.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{client.tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={cn(
                        "tabular-nums font-medium",
                        (client.balance || 0) < 0 && "text-destructive",
                        (client.balance || 0) > 0 && "text-green-600"
                      )}>
                        {(client.balance || 0).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="tabular-nums">{client.orders_count || 0}</span>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <ClientActions 
                        client={client} 
                        onEdit={handleEdit}
                        onView={handleView}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Client Dialog */}
      <AddClientDialog 
        open={dialogOpen} 
        onOpenChange={handleDialogClose}
        onSuccess={fetchClients}
        editClient={editClient}
      />

      {/* Client Detail Sheet */}
      <ClientDetailSheet
        client={viewClient}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onEdit={handleEdit}
        onRefresh={fetchClients}
        onCreateOrder={handleCreateOrder}
      />

      {/* Add Order Dialog (Quick Create from Client) */}
      <AddOrderDialog
        open={orderDialogOpen}
        onOpenChange={setOrderDialogOpen}
        onSuccess={handleOrderCreated}
        preselectedClient={orderClient}
      />

      {/* Broadcast Dialog */}
      <BroadcastDialog
        open={broadcastOpen}
        onOpenChange={setBroadcastOpen}
      />
    </div>
  );
}
